import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { RankBadge } from "@/components/RankBadge";
import { Search, UserPlus, Trash2, Edit2, Lock, Unlock, Plus, Minus, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminPlayers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [adjusting, setAdjusting] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/players?limit=500").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const filtered = list.filter((p) => p.nickname.toLowerCase().includes(q.toLowerCase()) || p.username.includes(q.toLowerCase()));

  const del = async (p) => {
    if (!window.confirm(`Xóa người chơi ${p.nickname}?`)) return;
    await api.delete(`/admin/players/${p.id}`);
    toast.success("Đã xóa");
    load();
  };

  const toggleActive = async (p) => {
    await api.patch(`/admin/players/${p.id}`, { active: !p.active });
    load();
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl">Quản lý người chơi</h1>
          <p className="text-white/40 text-sm mt-1">{filtered.length} / {list.length} người chơi</p>
        </div>
        <button onClick={() => setCreating(true)} className="px-4 py-2.5 rounded-md text-sm font-medium bg-gradient-to-r from-cyan-400 to-purple-500 text-black flex items-center gap-2" data-testid="create-player-btn">
          <UserPlus size={15} /> Tạo người chơi
        </button>
      </div>

      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          data-testid="players-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Tìm theo nickname hoặc username..."
          className="w-full bg-[#0c0d14] border border-white/10 rounded-md pl-9 pr-4 py-2.5 text-sm outline-none focus:border-cyan-400/60"
        />
      </div>

      <div className="rounded-xl bg-[#0c0d14] border border-white/8 overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-widest text-white/40 border-b border-white/8">
          <div className="col-span-4">Người chơi</div>
          <div className="col-span-2">Rank</div>
          <div className="col-span-1 text-right">ELO</div>
          <div className="col-span-1 text-right">Coin</div>
          <div className="col-span-1 text-right">W/L</div>
          <div className="col-span-1 text-right">Win%</div>
          <div className="col-span-2 text-right">Hành động</div>
        </div>
        {filtered.map((p) => (
          <div key={p.id} className="grid grid-cols-12 items-center px-5 py-3 border-b border-white/5 hover:bg-white/[0.03]" data-testid={`player-row-${p.username}`}>
            <div className="col-span-4 flex items-center gap-3 min-w-0">
              <img src={p.avatar_url} alt="" className="w-9 h-9 rounded-full" />
              <div className="min-w-0">
                <div className="text-sm truncate">{p.nickname} {!p.active && <span className="text-red-400 text-xs ml-1">[khóa]</span>}</div>
                <div className="text-xs text-white/40 truncate">@{p.username}</div>
              </div>
            </div>
            <div className="col-span-2"><RankBadge elo={p.elo} size="sm" showElo={false} /></div>
            <div className="col-span-1 text-right font-display font-semibold">{p.elo}</div>
            <div className="col-span-1 text-right text-yellow-300">{p.coin}</div>
            <div className="col-span-1 text-right text-xs"><span className="text-cyan-300">{p.wins}</span>/<span className="text-red-400">{p.losses}</span></div>
            <div className="col-span-1 text-right text-xs text-white/70">{p.win_rate}%</div>
            <div className="col-span-2 flex justify-end gap-1">
              <button onClick={() => setAdjusting(p)} className="p-2 rounded-md hover:bg-cyan-400/10 text-cyan-300" title="Cộng/trừ ELO·Coin" data-testid={`adjust-${p.username}`}><Plus size={14} /></button>
              <button onClick={() => setEditing(p)} className="p-2 rounded-md hover:bg-white/5 text-white/70" title="Sửa"><Edit2 size={14} /></button>
              <button onClick={() => toggleActive(p)} className="p-2 rounded-md hover:bg-white/5 text-white/70" title={p.active ? "Khóa" : "Mở khóa"}>{p.active ? <Lock size={14} /> : <Unlock size={14} />}</button>
              <button onClick={() => del(p)} className="p-2 rounded-md hover:bg-red-500/10 text-red-400" title="Xóa" data-testid={`delete-${p.username}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="py-12 text-center text-white/40 text-sm">Không có người chơi</div>}
      </div>

      {creating && <CreatePlayerModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {adjusting && <AdjustModal player={adjusting} onClose={() => setAdjusting(null)} onDone={() => { setAdjusting(null); load(); }} />}
      {editing && <EditPlayerModal player={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
    <div className="w-full max-w-md rounded-xl bg-[#0c0d14] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold">{title}</h3>
        <button onClick={onClose} className="p-1 hover:bg-white/5 rounded"><X size={16} /></button>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-wider text-white/50">{label}</span>
    <input {...props} className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
  </label>
);

const CreatePlayerModal = ({ onClose, onDone }) => {
  const [f, setF] = useState({ username: "", password: "player123", nickname: "", phone: "", avatar_url: "", elo: 1000 });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/players", { ...f, elo: Number(f.elo) });
      toast.success("Đã tạo người chơi");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lỗi");
    }
  };
  return (
    <Modal title="Tạo người chơi" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="create-player-form">
        <Field label="Username *" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} required />
        <Field label="Nickname *" value={f.nickname} onChange={(e) => setF({ ...f, nickname: e.target.value })} required />
        <Field label="Mật khẩu" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <Field label="SĐT" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <Field label="Avatar URL" value={f.avatar_url} onChange={(e) => setF({ ...f, avatar_url: e.target.value })} />
        <Field label="ELO khởi điểm" type="number" value={f.elo} onChange={(e) => setF({ ...f, elo: e.target.value })} />
        <button type="submit" data-testid="submit-create-player" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black">Tạo</button>
      </form>
    </Modal>
  );
};

const EditPlayerModal = ({ player, onClose, onDone }) => {
  const [f, setF] = useState({ nickname: player.nickname, phone: player.phone || "", avatar_url: player.avatar_url || "" });
  const submit = async (e) => {
    e.preventDefault();
    await api.patch(`/admin/players/${player.id}`, f);
    toast.success("Đã cập nhật");
    onDone();
  };
  return (
    <Modal title={`Sửa: ${player.nickname}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Nickname" value={f.nickname} onChange={(e) => setF({ ...f, nickname: e.target.value })} />
        <Field label="SĐT" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <Field label="Avatar URL" value={f.avatar_url} onChange={(e) => setF({ ...f, avatar_url: e.target.value })} />
        <button type="submit" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black">Lưu</button>
      </form>
    </Modal>
  );
};

const AdjustModal = ({ player, onClose, onDone }) => {
  const [elo, setElo] = useState(0);
  const [coin, setCoin] = useState(0);
  const submit = async () => {
    await api.post(`/admin/players/${player.id}/adjust`, { elo_delta: Number(elo), coin_delta: Number(coin) });
    toast.success("Đã cập nhật");
    onDone();
  };
  return (
    <Modal title={`Điều chỉnh: ${player.nickname}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="text-xs text-white/50">Hiện tại: <span className="text-white">{player.elo} ELO · {player.coin} coin</span></div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-white/50">Δ ELO (có thể âm)</label>
          <div className="flex items-center gap-2 mt-1">
            <button onClick={() => setElo(Number(elo) - 25)} className="p-2 rounded bg-white/5"><Minus size={14} /></button>
            <input type="number" value={elo} onChange={(e) => setElo(e.target.value)} data-testid="adjust-elo-input" className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-center" />
            <button onClick={() => setElo(Number(elo) + 25)} className="p-2 rounded bg-white/5"><Plus size={14} /></button>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-white/50">Δ Coin</label>
          <div className="flex items-center gap-2 mt-1">
            <button onClick={() => setCoin(Number(coin) - 10)} className="p-2 rounded bg-white/5"><Minus size={14} /></button>
            <input type="number" value={coin} onChange={(e) => setCoin(e.target.value)} data-testid="adjust-coin-input" className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-center" />
            <button onClick={() => setCoin(Number(coin) + 10)} className="p-2 rounded bg-white/5"><Plus size={14} /></button>
          </div>
        </div>
        <button onClick={submit} data-testid="adjust-submit" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black">Áp dụng</button>
      </div>
    </Modal>
  );
};
